import { ArrowRight, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/shared/components/button'
import { WarningTriangleIcon } from '@/shared/components/icons/warning-triangle'
import type { UserNoticeSource } from '../analytics'
import { useUserNoticeTracking } from '../hooks/use-user-notice-tracking'

interface UserNoticeCardProps {
  actionUrl: string
  source: UserNoticeSource
  onDismiss: () => void
}

// Profile variant: a card over the blurred page, close (×) top-right, no
// reassurance line. It closes and it renders the SOFT copy, so it reports SOFT —
// a dead-end cannot be shown behind a × in the first place.
export function UserNoticeCard({
  actionUrl,
  source,
  onDismiss,
}: UserNoticeCardProps) {
  const { t } = useTranslation()
  const { onClose, onUpdate } = useUserNoticeTracking({
    source,
    noticeType: 'soft',
    actionUrl,
    onDismiss,
  })

  return (
    // Above HelpPopup (z-[60]), which renders after this and would otherwise float on top.
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-white/60 px-[24px] backdrop-blur-md">
      <div className="max-h-[90vh] w-full max-w-sm overflow-y-auto rounded-[32px] bg-white p-[24px] shadow-[0_8px_32px_rgba(0,0,0,0.08)]">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            aria-label={t('user_notice_close')}
            className="flex size-8 cursor-pointer items-center justify-center text-app-grey-600"
          >
            <X className="h-[18px] w-[15px]" />
          </button>
        </div>

        <div className="flex flex-col items-center gap-[16px] text-center">
          <WarningTriangleIcon className="h-[80px] w-auto" />
          <span className="inline-flex items-center rounded-full border border-app-grey-300 px-[12px] py-[6px] text-sm font-medium text-app-primary-900">
            {t('user_notice_badge')}
          </span>
          <h2 className="font-display font-medium text-[32px] leading-[1.1] text-app-grey-900">
            {t('user_notice_title')}
          </h2>
          <p className="text-base leading-6 text-app-grey-700">
            {t('user_notice_subtitle')}
          </p>
        </div>

        <div className="pt-[24px]">
          <Button onClick={onUpdate}>
            {t('user_notice_cta')}
            <ArrowRight className="size-5" />
          </Button>
        </div>
      </div>
    </div>
  )
}
